import axios from "axios";
import eventEmitter from "./EventEmitter";
import { stringify } from "query-string";

import { token } from "./token";

const isStaging = process.env.ENVIRONMENT === "staging";
const stageBaseUrl = isStaging ? process.env.PROXY_PASS : "";

export const requests = axios.create({
  baseURL: `${stageBaseUrl}/api/v1`,
  paramsSerializer: params => {
    // по умолчанию массивы передаются вот так ids[]=1&ids[]=2
    // но в django формах удобнее когда вот так: ids=1&ids=2
    return stringify(params, {
      skipEmptyString: true,
      skipNull: true
    });
  }
});

requests.interceptors.request.use(
  config => {
    if (!token) {
      return config;
    }

    const newConfig = {
      headers: {},
      ...config
    };

    newConfig.headers.Authorization = `Token ${token}`;

    return newConfig;
  },
  e => Promise.reject(e)
);

requests.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (error?.response?.status === 401) {
      window.history.pushState(null, "", "/app/auth/login");
    }

    if (error.response.status == 500) {
      eventEmitter.emit("http500", error);
    } else {
      return Promise.reject(error);
    }
  }
);

const handleError = e => {
  if (e.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx

    throw {
      status: e.response.status,
      message: e.response.data.message,
      errors: e.response.data.errors,
      code: e.response.data.code
    };
  } else {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    // ---
    // Something happened in setting up the request that triggered an Error
    throw e;
  }
};

export const CardsService = async params => {
  const response = await requests.get("/cards", { params });
  return response.data;
};

export const CardTakeOnService = async ({ id, userId }) => {
  const data = { user_id: userId };

  try {
    return await requests.post(`/cards/${id}/take_on`, data);
  } catch (e) {
    handleError(e);
  }
};

export const CardTakeOffService = async ({ id }) => {
  const response = await requests.post(`/cards/${id}/take_off`);
  return response.data;
};

export const CardSetRatioService = async ({ id, ratio }) => {
  const data = { ratio };
  const response = await requests.post(`/cards/${id}/set_ratio`, data);
  return response.data;
};

export const CardSetPacketService = async ({ id, packet }) => {
  const data = { packet };
  const response = await requests.post(`/cards/${id}/set_packet`, data);
  return response.data;
};

export const MyCardsService = async () => {
  const response = await requests.get("/cards/my");
  return response.data.cards;
};

export const TeaserNotificationService = async () => {
  const response = await requests.get("/categories/troubles");
  return response.data;
};

const parseUser = user => {
  if (user === "" || user === null || user === undefined) {
    return [];
  }

  if (typeof user === "string") {
    return user.split(",").map(Number);
  }

  return Array.isArray(user) ? user : [user];
};

export const TeasersService = async params => {
  try {
    const response = await requests.get("/teasers", {
      params: {
        ...params,
        user: parseUser(params.user)
      }
    });
    return {
      teasers: response.data.teasers,
      total_count: response.data.total_count,
      total_stat: response.data.total_stat
    };
  } catch (e) {
    handleError(e);
  }
};

export const TeasersSummaryStat = async params => {
  const response = await requests.get("/teasers/summary_stat", {
    params: {
      ...params,
      user: parseUser(params.user)
    }
  });
  return response.data;
};

export const CampaignSuggestService = async params => {
  const response = await requests.get("/campaigns/suggest", { params });
  return response.data;
};

export const CampaignOverviewService = async ({ cid }) => {
  const response = await requests.get(`/campaigns/${cid}/overview`);
  return response.data;
};

export const UserSuggestService = async params => {
  const response = await requests.get("/users/suggest", { params });
  return response.data;
};

export const TeasersCountService = async ({ ids, globalCheck, search }) => {
  const params = { ids, global_check: globalCheck, ...search };

  const response = await requests.get("/teasers/count", { params });
  return response.data;
};

export const BatchStatusChangeService = async ({
  ids,
  globalCheck,
  search,
  status
}) => {
  const params = { ids, global_check: globalCheck, ...search };
  const data = { status };

  const response = await requests.request({
    url: "/teasers/batch/status",
    method: "post",
    params,
    data
  });

  return response.data;
};

export const TeasersCopyService = async ({
  ids,
  globalCheck,
  search,
  action,
  fields
}) => {
  const params = { ids, global_check: globalCheck, ...search };
  const data = fields;

  const response = await requests.request({
    url: "/teasers/batch/copy",
    method: "post",
    params,
    data
  });

  return response.data;
};

export const CpcChangeService = async ({ tid, cpc }) => {
  const response = await requests.post(`/teasers/${tid}/cpc`, { cpc });
  return response.data;
};

export const StatResetService = async ({ tid }) => {
  const response = await requests.post(`/teasers/${tid}/stat_reset`);
  return response.data;
};

export const StatusChangeService = async ({ tid, status }) => {
  const response = await requests.post(`/teasers/${tid}/status`, {
    published: status
  });
  return response.data;
};

export const ShowTypeChangeService = async ({ tid, type }) => {
  const response = await requests.post(`/teasers/${tid}/show_type`, { type });
  return response.data;
};

export const SetPayoutService = async ({
  tid,
  reasons,
  payoutStatus,
  payoutStatusOld
}) => {
  try {
    const response = await requests.post(`/payouts/teasers/${tid}/set`, {
      payout_reasons: reasons,
      payout_status: payoutStatus,
      payout_status_old: payoutStatusOld
    });

    return response.data;
  } catch (e) {
    handleError(e);
  }
};

export const SetGradeStatusService = async ({
  tid,
  gradeStatus,
  gradeStatusOld
}) => {
  try {
    const response = await requests.post(`/payouts/teasers/${tid}/set/grade`, {
      grade_status: gradeStatus,
      grade_status_old: gradeStatusOld
    });

    return response.data;
  } catch (e) {
    handleError(e);
  }
};

export const SetNotificationReadService = async ({ ids }) => {
  const response = await requests.post("/notifications/set_read", { ids });
  return response.data;
};

export const NotificationListService = async () => {
  const response = await requests.get("/notifications");
  return response.data;
};

export const GetHeaderState = () => {
  return (
    JSON.parse(localStorage.getItem("headerState")) || {
      opened: true,
      fixed: false
    }
  );
};

export const SetHeaderState = state => {
  localStorage.setItem("headerState", JSON.stringify(state));
};

export const TeaserSaveService = async data => {
  try {
    const response = await requests.post("/teasers/create", data);
    return response.data;
  } catch (e) {
    handleError(e);
  }
};

export const TeaserTitleGenerateService = async itemId => {
  try {
    const response = await requests.post(`/scrapper/${itemId}/generate_title`);
    return response.data;
  } catch (e) {
    handleError(e);
  }
};

export const UrlImageUploadService = async ({ url }) => {
  try {
    const response = await requests.post("/image/upload/url", { image: url });
    return response.data;
  } catch (e) {
    handleError(e);
  }
};

export const Url2Base64Service = async ({ url }) => {
  try {
    const response = await requests.get(
      `/image/url2base64?url=${encodeURIComponent(url)}`
    );
    return response.data;
  } catch (e) {
    handleError(e);
  }
};

export const ImageUploadService = async ({ data }) => {
  try {
    const response = await requests.post("/image/upload", data);
    return response.data;
  } catch (e) {
    handleError(e);
  }
};

export const TeaserDetailService = async ({ tid }) => {
  const response = await requests.get(`/teasers/${tid}`);
  return response.data;
};

export const TeaserUpdateService = async ({ tid, data }) => {
  try {
    const response = await requests.post(`/teasers/${tid}/update`, data);
    return response.data;
  } catch (e) {
    handleError(e);
  }
};

export const TeaserListDetailService = async ({ tid }) => {
  const response = await requests.get(`/teasers/${tid}/list_detail`);
  return response.data;
};

export const ScraperItemsListService = async ({
  campaign,
  period,
  limit,
  offset
}) => {
  const params = { campaign, period, limit, offset };

  const response = await requests.get("/scraper/items", { params });

  return response.data;
};

export const ScraperItemsRefreshService = async ({ campaign }) => {
  const response = await requests.post("/scraper/items/refresh", { campaign });
  return response.data;
};

export const TeaserStatService = async ({
  tid,
  detail,
  date_from,
  date_to
}) => {
  const params = { detail, date_from, date_to };
  const url = `/teasers/${tid}/stat`;

  const response = await requests.get(url, { params });

  return response.data;
};

export const TeaserModerationCountService = async () => {
  const response = await requests.get("/teasers/moderation/count");
  return response.data;
};

export const TeaserForModerationService = async ({ group, exclude = [] }) => {
  const url = `/teasers/moderation/${group}/next`;
  const params = { exclude };

  const response = await requests.get(url, { params });
  return response.data;
};

export const TeaserModerateService = async ({ tid, data }) => {
  try {
    const response = await requests.post(`/teasers/${tid}/moderate`, data);
    return response.data;
  } catch (e) {
    handleError(e);
  }
};

export const CommonPayoutsService = async ({ user, date_from, date_to }) => {
  const params = { user, date_from, date_to };
  const response = await requests.get("/payouts/common", { params });
  return response.data;
};

export const TeasersPayoutsService = async ({ user, status, date }) => {
  const params = { user, status, date };
  const response = await requests.get("/payouts/teasers", { params });
  return response.data;
};

export const BuyoutStatService = async ({
  date_from,
  date_to,
  slot,
  status
}) => {
  const params = { date_from, date_to, slot, status };
  const response = await requests.get("/buyout/stat", { params });
  return response.data;
};

export const BuyoutGroupsStatService = async params => {
  const response = await requests.get("/buyout/groups/stat", { params });
  return response.data;
};

export const BuyoutSlotsService = async ({ q }) => {
  const params = { q };
  const response = await requests.get("/buyout/slots", { params });
  return response.data;
};

export const BulkStatusChangeById = async ({ status, teaser_ids }) => {
  const data = { status, teaser_ids };
  const response = await requests.post(
    "/teasers/bulk/status_change_by_id",
    data
  );
  return response.data;
};

export const NewsStatService = async ({
  status,
  cid,
  d1start,
  d1end,
  d2start,
  d2end
}) => {
  const params = { status, cid, d1start, d1end, d2start, d2end };
  const response = await requests.get("/news/stat", { params });
  return response.data;
};

export const CategoriesService = async ({ group }) => {
  const params = { group };
  const response = await requests.get("/categories", { params });
  return response.data;
};

export const ShowsGroupsService = async ({ show_type, group }) => {
  const url = `/shows_groups/${show_type}/${group}`;
  const response = await requests.get(url);
  return response.data;
};

export const CardSetHiddenService = async ({ cid, hidden }) => {
  const url = `/cards/${cid}/set_hidden`;
  const data = { hidden };

  const response = await requests.post(url, data);

  return response.data;
};

export const PermList = async ({ user_id }) => {
  const response = await requests.get(`/perm_list/${user_id}`);
  return response.data;
};

export const PermSet = async ({ user_id, perm_id, checked }) => {
  const data = { user_id, perm_id, checked };
  const response = await requests.post("perm_change", data);
  return response.data;
};

export const B64Upload = async image => {
  const data = { image };
  const response = await requests.post("image/b64", data);
  return response.data;
};

export const SetCidWorkOnService = async cid => {
  await requests.post("/teasers/set_cid_work_on", { cid });
};

export const ChangeLogListService = async params => {
  const response = await requests.get("/change_log", { params });
  return response.data;
};

export const init = async (params = {}) => {
  const { data } = await requests.get("/init", {
    params: {
      ...params,
      teasers_user: parseUser(params.teasers_user)
    }
  });

  return data;
};

export const login = async payload => {
  const { data } = await requests.post("/user/token", payload);

  return data;
};

export const logout = async () => await requests.post("/user/logout");

export const copyTeasersMpaaRating = async data =>
  await requests.post("/teasers/batch/mpaa", data);
